import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import {
  useAddHomework,
  useHomeworkList,
  useUpdateHomework,
} from "~/lib/queries/homeworkQuery";
import {
  allEngagementSessionsQueryOptions,
  useAllEngagementSessions,
} from "~/lib/queries/sessionQuery";
import type {
  AddHomeworkInput,
  HomeworkDraft,
  UpdateHomeworkInput,
} from "~/lib/schemas/homework";

interface EditingHomework extends HomeworkDraft {
  id: string;
}
import { HomeworkAddDrawer } from "~/components/engagement/homework/homework-add-drawer";
import { HomeworksList } from "~/components/engagement/homework/homeworks-list";
import { ScheduledHomeworkList } from "~/components/engagement/homework/scheduled-homework-list";

export const Route = createFileRoute(
  "/_user/my-coaching/clients/programs/$engagementId/homework",
)({
  staticData: { title: "Homework" },
  validateSearch: z.object({
    create: z.boolean().optional(),
    editId: z.string().optional(),
  }),
  component: HomeworkPage,
});

function HomeworkPage() {
  const { engagementId } = Route.useParams();
  const { create, editId } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: homeworkData, isLoading: homeworkLoading } =
    useHomeworkList(engagementId);

  // Prefer cached sessions (already loaded by parent); fall back to live query
  const cachedSessionData = queryClient.getQueryData(
    allEngagementSessionsQueryOptions(engagementId).queryKey,
  );
  const { data: liveSessionData } = useAllEngagementSessions(engagementId);
  const sessionData = cachedSessionData ?? liveSessionData;
  const availableSessions = sessionData?.sessions ?? [];

  const addHomeworkMutation = useAddHomework();
  const updateHomeworkMutation = useUpdateHomework();

  const [homeworkDrawerOpen, setHomeworkDrawerOpen] = useState(false);
  const [editingHomework, setEditingHomework] =
    useState<EditingHomework | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  // Auto-open add drawer when ?create=true
  useEffect(() => {
    if (create) {
      setEditingHomework(null);
      setHomeworkDrawerOpen(true);
      // Clear create from URL to prevent re-opening on data refetch
      navigate({
        to: ".",
        search: { create: undefined, editId: undefined },
        replace: true,
      });
    }
  }, [create, navigate]);

  // Auto-open edit drawer when ?editId=<id> and data is loaded
  useEffect(() => {
    if (!editId || !homeworkData?.homeworks) return;
    const match = homeworkData.homeworks.find((hw) => hw.id === editId);
    if (match) {
      setEditingHomework({
        id: match.id,
        title: match.title,
        description: match.description ?? undefined,
        deadline: match.deadline,
        sessionId: match.session?.id,
        engagementId,
      });
      setHomeworkDrawerOpen(true);
      // Clear editId from URL to prevent re-opening on data refetch
      navigate({
        to: ".",
        search: { create: undefined, editId: undefined },
        replace: true,
      });
    }
  }, [editId, homeworkData, navigate]);

  const handleAddHomework = () => {
    setEditingHomework(null);
    setHomeworkDrawerOpen(true);
  };

  const handleEditHomework = (homework: {
    id: string;
    title: string;
    description?: string | null;
    deadline: Date | null;
    sessionId?: string;
  }) => {
    setEditingHomework({
      id: homework.id,
      title: homework.title,
      description: homework.description ?? undefined,
      deadline: homework.deadline,
      sessionId: homework.sessionId,
      engagementId,
    });
    setHomeworkDrawerOpen(true);
  };

  const handleHomeworkDrawerClose = (open: boolean) => {
    if (!open) {
      setHomeworkDrawerOpen(false);
      setEditingHomework(null);
      setCreateError(null);
    }
  };

  const handleHomeworkSubmit = (data: AddHomeworkInput) => {
    setCreateError(null);
    const isEditing = editingHomework !== null;

    if (isEditing) {
      const updateData: UpdateHomeworkInput = {
        id: editingHomework.id,
        title: data.title,
        description: data.description,
        deadline: data.deadline,
        sessionId: data.sessionId,
        trigger: data.trigger,
        goalId: data.goalId,
      };
      updateHomeworkMutation.mutate(updateData, {
        onSuccess: () => {
          setHomeworkDrawerOpen(false);
          setEditingHomework(null);
          toast.success("Homework updated");
        },
        onError: (error) => {
          const msg = error?.message ?? "Failed to update homework";
          setCreateError(msg);
          toast.error(msg);
        },
      });
    } else {
      addHomeworkMutation.mutate(
        { ...data, engagementId },
        {
          onSuccess: () => {
            setHomeworkDrawerOpen(false);
            toast.success("Homework added");
          },
          onError: (error) => {
            const msg = error?.message ?? "Failed to create homework";
            setCreateError(msg);
            toast.error(msg);
          },
        },
      );
    }
  };

  return (
    <div className="space-y-8">
      <HomeworksList
        homeworks={homeworkData?.homeworks}
        progress={homeworkData?.progress}
        homeworkLoading={homeworkLoading}
        onAddHomework={handleAddHomework}
        onEditHomework={handleEditHomework}
        availableSessions={availableSessions}
      />
      <ScheduledHomeworkList engagementId={engagementId} />
      <HomeworkAddDrawer
        open={homeworkDrawerOpen}
        onOpenChange={handleHomeworkDrawerClose}
        onSubmit={handleHomeworkSubmit}
        engagementId={engagementId}
        availableSessions={availableSessions}
        createError={createError}
        editingHomework={editingHomework}
      />
    </div>
  );
}
